<template>
  <div>
    <div class="search-area">
      <form class="search-form" v-on:submit.prevent="doSearch">
        <div class="form-group">
          <label class="form-control-label keyword">キーワード</label>
          <input
            type="text"
            class="form-control"
            v-model="state.search.tagName"
          >
        </div>
        <div class="form-group">
          <label class="form-control-label">期間</label>
          <div>
            <MyCalendar
              class="date-from"
              :date-value="state.search.dtFrom"
              @date-input="state.search.dtFrom = $event"
            />
            <span class="ib waveline ta-center ml4 mr4">〜</span>
            <MyCalendar
              class="date-to"
              :date-value="state.search.dtTo"
              @date-input="state.search.dtTo = $event"
            />
          </div>
        </div>
        <div class="form-group tag-movie-num-range">
          <label class="form-control-label">登録動画数</label>
          <div>
            <input
              v-model="state.search.tagMovieNumFrom"
              type="text"
              class="ib form-control tag-movie-num-from"
            />
            <span class="ib waveline ta-center ml4 mr4">〜</span>
            <input
              v-model="state.search.tagMovieNumTo"
              type="text"
              class="ib form-control tag-movie-num-to"
            />
          </div>
        </div>
        <div class="form-group search-btn ta-center">
          <button type="submit" class="btn btn-primary">検索</button>
        </div>
      </form>
    </div>
    <AdminListTable
      :display-data="displayData"
      :fields="fields"
      :custom-buttons="customButtons"
      :can-delete="canDelete"
      :can-edit="canEdit"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />
    <Modal
      v-if="state.showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="state.showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p1">動画タグ<span class="colon"></span>{{state.destroyingTag?.tagName}}</p>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg"></p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import {
  TAG_TYPE1_SYSTEM,
  TAG_TYPE1_USER,
} from 'src/consts/tag';
import type { AdminTagIndexItem } from 'src/models/tag';
import type { AdminListTableField, CustomButton } from 'src/models/adminListTable';

type DisplayData = AdminTagIndexItem & {
  tagTypeLabel: string;
};

interface SearchParams {
  tagName: string;
  dtFrom: string | null;
  dtTo: string | null;
  tagMovieNumFrom: string;
  tagMovieNumTo: string;
}

interface TagTableState {
  search: SearchParams;
  // 検索ボタン押下時に確定する検索条件。フィルタはこちらのみを参照する（入力中は自動検索しない）
  applied: SearchParams;
  showDestroyModal: boolean;
  destroyingTag: AdminTagIndexItem | null;
}

const initSearchParams = (): SearchParams => ({
  tagName: '',
  dtFrom: null,
  dtTo: null,
  tagMovieNumFrom: '',
  tagMovieNumTo: '',
});

const props = defineProps<{
  tags: AdminTagIndexItem[];
}>();

const emit = defineEmits<{
  (e: 'edit', tag: AdminTagIndexItem): void;
  (e: 'destroy', tag: AdminTagIndexItem): void;
  (e: 'merge', tag: AdminTagIndexItem): void;
  (e: 'search'): void;
}>();

const state = reactive<TagTableState>({
  search: initSearchParams(),
  applied: initSearchParams(),
  showDestroyModal: false,
  destroyingTag: null,
});

const doSearch = (): void => {
  emit('search');
  state.applied = { ...state.search };
};

const isSystem = (tag: AdminTagIndexItem): boolean => {
  return tag.tagType1 === TAG_TYPE1_SYSTEM;
};

const getTagTypeLabel = (type: string): string => {
  if (type === TAG_TYPE1_SYSTEM) return 'システム';
  if (type === TAG_TYPE1_USER) return 'ユーザー';

  return type;
};

const filteredTags = computed<AdminTagIndexItem[]>(() => {
  const qTagName = state.applied.tagName;
  const qDtFrom = new Date(state.applied.dtFrom ? state.applied.dtFrom : '');
  const qDtTo = new Date(state.applied.dtTo ? state.applied.dtTo : '');
  const qTagMovieNumFrom = parseInt(state.applied.tagMovieNumFrom ? state.applied.tagMovieNumFrom : '');
  const qTagMovieNumTo = parseInt(state.applied.tagMovieNumTo ? state.applied.tagMovieNumTo : '');
  return props.tags.filter(tag => {
    if (qTagName && tag.tagName.indexOf(qTagName) < 0) return false;

    const ts = new Date(tag.createdAtDate);
    if (qDtFrom && ts < qDtFrom) return false;

    if (qDtTo && ts > qDtTo) return false;

    if (!isNaN(qTagMovieNumFrom) && tag.tagMovieNum < qTagMovieNumFrom) return false;

    if (!isNaN(qTagMovieNumTo) && tag.tagMovieNum > qTagMovieNumTo) return false;

    return true;
  });
});

const displayData = computed<DisplayData[]>(() => {
  return filteredTags.value.map(tag => {
    return {
      ...tag,
      tagTypeLabel: getTagTypeLabel(tag.tagType1),
    };
  });
});

const edit = (obj: AdminTagIndexItem) => {
  emit('edit', obj);
};

const tryDestroy = (obj: AdminTagIndexItem) => {
  state.destroyingTag = obj;
  state.showDestroyModal = true;
};

const doDestroy = () => {
  if (!state.destroyingTag) return;

  emit('destroy', state.destroyingTag);
  state.showDestroyModal = false;
};

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const fields: AdminListTableField<DisplayData>[] = [
  { name: 'tagName', label: '動画タグ' },
  { name: 'tagTypeLabel', label: '種類' },
  { name: 'createdAtDate', label: '作成日' },
  { name: 'tagMovieNum', label: '登録動画数' },
];
const customButtons: CustomButton<AdminTagIndexItem>[] = [
  {
    label: '名寄せ',
    clickHandler: (obj: AdminTagIndexItem) => {
      emit('merge', obj);
    },
    isVisible: (obj: AdminTagIndexItem) => !isSystem(obj),
    isDisabled: () => false,
  },
];
const canDelete = (obj: AdminTagIndexItem) => !isSystem(obj);
const canEdit = (obj: AdminTagIndexItem) => !isSystem(obj);
</script>

<style lang="scss" scoped>
.search-form {
  .form-group {
    .tag-movie-num-from, .tag-movie-num-to {
      &.form-control {
        width: 60px !important;
      }
    }
  }
}
</style>
